import { useMemo, useState } from "react";
import { Document, Page } from "react-pdf";
import { Button } from "@/components/ui/button";
import { DocumentViewer } from "@/components/ui/document-viewer";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fileLabel } from "./bundleText";
import { parseJsonDocument } from "./jsonDocument";
import { JsonDocumentViewer } from "./JsonDocumentViewer";
import { SkippedPageBanner } from "./SkippedPageBanner";
import { useBundleFile } from "./useBundleFile";
import type { RawFilesBundleFile } from "./useBundlesForPatient";

interface BundleFileTabsProps {
  bundleId: string;
  files: RawFilesBundleFile[];
  /** Page numbers the summary did not read, by file key. */
  skippedPagesByFile?: ReadonlyMap<string, readonly number[]>;
}

interface BundleFileTabContentProps {
  bundleId: string;
  fileKey: string;
  active: boolean;
  skippedPages: readonly number[];
}

function PdfViewer({ content, skippedPages }: { content: string; skippedPages: readonly number[] }) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const file = useMemo(() => ({ data: atob(content) }), [content]);

  const isSkipped = skippedPages.includes(pageNumber);

  return (
    <div className="space-y-3">
      {isSkipped && <SkippedPageBanner />}
      <Document
        file={file}
        loading={<div>Loading PDF...</div>}
        error={<div>Failed to load PDF.</div>}
        onLoadSuccess={({ numPages: loadedNumPages }) => {
          setNumPages(loadedNumPages);
          setPageNumber(1);
        }}
      >
        <Page pageNumber={pageNumber} />
      </Document>

      {numPages !== null && (numPages > 1 || isSkipped) && (
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {pageNumber} of {numPages}
            {isSkipped && " (skipped)"}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={pageNumber >= numPages}
            onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

function TextOrJsonViewer({ content }: { content: string }) {
  const parsed = useMemo(() => parseJsonDocument(content), [content]);

  if (parsed === null) {
    return <DocumentViewer content={content} className="text-sm" markdown={false} />;
  }

  return <JsonDocumentViewer json={parsed} raw={content} />;
}

function BundleFileTabContent({ bundleId, fileKey, active, skippedPages }: BundleFileTabContentProps) {
  const { data, isLoading, error } = useBundleFile(bundleId, fileKey, active);

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-8 text-center">Loading document…</div>;
  }

  if (error) {
    return <div className="text-destructive py-8 text-center">{error.message}</div>;
  }

  if (!data) {
    return null;
  }

  if (data.type === "pdf") {
    return <PdfViewer content={data.content} skippedPages={skippedPages} />;
  }

  if (data.type === "jpeg") {
    return (
      <div className="space-y-4">
        {skippedPages.includes(1) && <SkippedPageBanner />}
        <img src={`data:image/jpeg;base64,${data.content}`} alt={fileKey} className="max-w-full" />
      </div>
    );
  }

  if (data.type === "png") {
    return (
      <div className="space-y-4">
        {skippedPages.includes(1) && <SkippedPageBanner />}
        <img src={`data:image/png;base64,${data.content}`} alt={fileKey} className="max-w-full" />
        {data.sidecarText && (
          <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Mediviewer-supplied OCR text
            </p>
            <DocumentViewer content={data.sidecarText} className="text-sm" markdown={false} />
          </div>
        )}
      </div>
    );
  }

  return <TextOrJsonViewer content={data.content} />;
}

const renderErrorFallback = (
  <div className="text-center text-destructive py-8">This document could not be displayed.</div>
);

export function BundleFileTabs({ bundleId, files, skippedPagesByFile }: BundleFileTabsProps) {
  const [activeTab, setActiveTab] = useState(files[0]?.key ?? "");

  if (files.length === 0) {
    return <div className="text-sm text-muted-foreground">No files in this bundle.</div>;
  }

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="flex h-auto w-full flex-wrap gap-1">
        {files.map((file) => (
          <TabsTrigger key={file.key} value={file.key} title={file.key} className="text-xs max-w-[220px]">
            <span className="block truncate text-left">{fileLabel(file.key)}</span>
          </TabsTrigger>
        ))}
      </TabsList>

      {/* forceMount + CSS hide (not conditional render) keeps every tab's useBundleFile call mounted across renders, satisfying the hooks rule, while `active` gates which one's query is enabled */}
      {files.map((file) => (
        <TabsContent key={file.key} value={file.key} forceMount className="data-[state=inactive]:hidden">
          <div className="border rounded-lg bg-white p-4">
            <ErrorBoundary fallback={renderErrorFallback}>
              <BundleFileTabContent
                bundleId={bundleId}
                fileKey={file.key}
                active={activeTab === file.key}
                skippedPages={skippedPagesByFile?.get(file.key) ?? []}
              />
            </ErrorBoundary>
          </div>
        </TabsContent>
      ))}
    </Tabs>
  );
}
